import { GetServerSidePropsContext } from 'next';
import { MarketplaceRedirect } from './company.types';

export type NextRedirect = { redirect: { permanent: boolean; destination: string } };

/**
 * Turns the backend's redirect decision into a Next.js redirect, preserving the
 * original path (minus the country prefix already included in redirectUrl) and
 * the query string so deep links survive.
 *
 * Example:
 *   In:  https://foo.site.agendaprodev.com/cl/sign_consents/my_consent?code=abc
 *   Out: https://www.agendaprodev.com/site/cl/foo/sign_consents/my_consent?code=abc
 */
export const buildMarketplaceRedirect = (
  ctx: GetServerSidePropsContext,
  redirect?: MarketplaceRedirect,
): NextRedirect | null => {
  if (!redirect?.needsRedirect || !redirect?.redirectUrl) {
    return null;
  }

  const base = redirect.redirectUrl.replace(/\/$/, '');
  // E-commerce routes start with /[countryCode]/..., and the e-commerce uses
  // /sucursal/<id> while the marketplace uses /<id> directly. We strip both
  // segments so the deep link resolves to the correct marketplace URL.
  const rawPath = (ctx.req.url ?? '').replace(/^\/[^/?]+(\/sucursal)?/, '');
  const pathWithoutCountry = rawPath === '/' ? '' : rawPath;

  return {
    redirect: {
      permanent: false, // 302: site_type can change if the company upgrades/downgrades plan
      destination: `${base}${pathWithoutCountry}`,
    },
  };
};
